import { useCallback, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";

/**
 * Antes de subir un Excel que puede referenciar varios abogados distintos
 * (Órdenes de Pago por archivo, Demandas, Cédulas, Escritos generados, Carga
 * de Expedientes), hay que pedir la sesión de CADA UNO al que le falte,
 * de a uno en fila, ANTES de disparar el procesamiento real — pedirla recién
 * cuando el job ya está corriendo en background siempre llega tarde (la
 * sesión ya está vencida para cuando se necesita).
 *
 * Uso:
 *   const sessionQueue = useLawyerSessionQueue();
 *   ...
 *   await sessionQueue.checkAndRun(
 *     () => previewXyzLawyers(formData),   // debe resolver a {data:{data:[{id,name,lastname,cached}]}}
 *     () => submitFile()                    // se llama automático cuando ya no falta ninguno
 *   );
 *   ...
 *   <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
 */
export default function useLawyerSessionQueue() {
  const { showToast } = useToast();
  const [pendingLawyers, setPendingLawyers] = useState([]);
  const [pendingIndex, setPendingIndex] = useState(0);
  const onReadyRef = useRef(null);

  const checkAndRun = useCallback(
    async (previewFn, onReady) => {
      const res = await previewFn();
      const lawyers = res.data?.data || [];
      const missing = lawyers.filter((l) => !l.cached);

      if (missing.length > 0) {
        onReadyRef.current = onReady;
        setPendingLawyers(missing);
        setPendingIndex(0);
      } else {
        await onReady();
      }
    },
    []
  );

  const currentPendingLawyer = pendingLawyers[pendingIndex] || null;

  const handleCaptured = useCallback(() => {
    setPendingIndex((prevIndex) => {
      const next = prevIndex + 1;
      if (next < pendingLawyers.length) {
        return next;
      }
      // Última sesión pegada → recién ahora se dispara la acción real.
      const onReady = onReadyRef.current;
      onReadyRef.current = null;
      setPendingLawyers([]);
      onReady?.();
      return 0;
    });
  }, [pendingLawyers.length]);

  const handleCancel = useCallback(() => {
    onReadyRef.current = null;
    setPendingLawyers([]);
    setPendingIndex(0);
    showToast(
      "Carga cancelada — faltaba pegar la sesión de uno o más abogados.",
      "info"
    );
  }, [showToast]);

  const progressLabel =
    pendingLawyers.length > 1 ? `${pendingIndex + 1} de ${pendingLawyers.length}` : null;

  return {
    checkAndRun,
    isPending: pendingLawyers.length > 0,
    currentPendingLawyer,
    progressLabel,
    dialogProps: {
      open: Boolean(currentPendingLawyer),
      onClose: handleCancel,
      lawyer: currentPendingLawyer,
      onCaptured: handleCaptured,
      progressLabel,
    },
  };
}
